import { useEffect, useRef } from 'react'

import type { PendoProviderVisitor } from '../common/utils/pendoProviderMetadata'

type UseProviderPendoVisitorArgs = {
  isLoggedIn: boolean
  isReady: boolean
  visitor?: PendoProviderVisitor
}

export const useProviderPendoVisitor = ({ isLoggedIn, isReady, visitor }: UseProviderPendoVisitorArgs) => {
  // The agent can only be initialized once per document, and logout/login is in-SPA, so this must
  // survive a logout. The visitor id is tracked separately to tell a metadata update from an
  // identity change.
  const hasInitializedAgent = useRef(false)
  const initializedVisitorId = useRef<string | undefined>(undefined)

  useEffect(() => {
    if ((window as Window & { Cypress?: unknown }).Cypress) {
      return
    }

    const pendo = window.pendo
    if (!isLoggedIn) {
      if (initializedVisitorId.current) {
        pendo?.clearSession?.()
        initializedVisitorId.current = undefined
      }
      return
    }

    if (!isReady || !visitor || !pendo) {
      return
    }

    try {
      if (!hasInitializedAgent.current) {
        if (!pendo.initialize) return
        pendo.initialize({ visitor })
        hasInitializedAgent.current = true
      } else if (initializedVisitorId.current !== visitor.id) {
        // A different provider, or a re-login after clearSession(). Either way the agent is
        // already initialized, so identify() rather than initializing a second time.
        if (!pendo.identify) return
        pendo.identify({ visitor })
      } else if (pendo.updateOptions) {
        pendo.updateOptions({ visitor })
      } else {
        if (!pendo.identify) return
        pendo.identify({ visitor })
      }

      initializedVisitorId.current = visitor.id
    } catch (error) {
      console.error('Failed to update Pendo visitor:', error)
    }
  }, [isLoggedIn, isReady, visitor])
}
